import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { serverTimestamp } from '@angular/fire/firestore';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular/standalone';
import { TranslateService } from '@ngx-translate/core';
import { LoadingComponent } from '@scandium-oy/ngx-scandium';
import { shareReplay, take } from 'rxjs/operators';
import { AppCommonModule } from 'src/app/common.module';
import { LoadingButtonComponent } from 'src/app/components/loading-button/loading-button.component';
import { MainDialogComponent } from 'src/app/components/main-dialog/main-dialog.component';
import { SegmentListComponent } from 'src/app/components/segment-list/segment-list.component';
import { SelectAreaButtonComponent } from 'src/app/components/select-area/select-area.component';
import { SelectSiteButtonComponent } from 'src/app/components/select-site/select-site.component';
import { SpeechToTextComponent } from 'src/app/components/speech-to-text/speech-to-text.component';
import { News } from 'src/app/models/news.model';
import { SiteArea } from 'src/app/models/site-area.model';
import { Site } from 'src/app/models/site.model';
import { AiService } from 'src/app/services/ai.service';
import { ClientsService } from 'src/app/services/clients.service';
import { SitesService } from 'src/app/services/sites.service';
import { UsersService } from 'src/app/services/users.service';

@Component({
  selector: 'app-new-news-dialog',
  templateUrl: './new-news.dialog.html',
  styleUrls: ['./new-news.dialog.scss'],
  imports: [
    CommonModule,
    AppCommonModule,
    MainDialogComponent,
    SelectSiteButtonComponent,
    SelectAreaButtonComponent,
    LoadingComponent,
    LoadingButtonComponent,
    SpeechToTextComponent,
    SegmentListComponent,
  ],
})
export class NewNewsDialogComponent {
  private aiService = inject(AiService);
  private translate = inject(TranslateService);
  private sitesService = inject(SitesService);

  formGroup: FormGroup;
  selectedSite = signal<Site>(null);

  sites$ = this.sitesService.getActiveList().pipe(
    shareReplay(1),
  );

  selectedSites = signal<Site[]>([]);
  selectedAreas = signal<SiteArea[]>([]);
  loading = signal(false);

  segment = signal('normal');
  segmentButtons = computed(() => [
    {
      value: 'normal',
      name: this.translate.instant('news.normal'),
      show: true,
    },
    {
      value: 'ai',
      name: this.translate.instant('news.ai'),
      icon: 'color-wand-outline',
      show: true,
    },
  ]);

  constructor(
    private clientsService: ClientsService,
    private formBuilder: FormBuilder,
    private _modal: ModalController,
    private usersService: UsersService,
  ) {
    this.formGroup = this.formBuilder.group({
      title: ['', Validators.required],
      text: ['', Validators.required],
      client: [this.clientsService.clientS()?.guid ?? null],
      sendEmail: [false],
    });
  }

  onSites(sites: Site[]) {
    this.selectedSites.set(sites);
  }

  onArea(areas: SiteArea[]) {
    this.selectedAreas.set(areas);
  }

  useAI() {
    const { text } = this.formGroup.value;
    this.loading.set(true);
    this.aiService.createNews(text).pipe(
      take(1),
    ).subscribe({
      next: (res) => {
        this.formGroup.get('title').setValue(res.title);
        this.formGroup.get('text').setValue(res.text);
        this.loading.set(false);
      },
      error: (error) => {
        console.error(error);
        this.loading.set(false);
      },
    });
  }

  onMic(text: string) {
    const prevText = this.formGroup.get('text').value as string;
    this.formGroup.get('text').setValue(prevText ? `${prevText} ${text}` : text);
  }

  save() {
    const user = this.usersService.currentUserS();
    const { title, text, client, sendEmail } = this.formGroup.value;
    const item: News = {
      created: serverTimestamp(),
      user: user.guid,
      message: {
        fi: text,
      },
      title: {
        fi: title,
      },
      areas: this.selectedAreas().map((it) => it.guid),
      sites: this.selectedSites().map((it) => it.guid),
      client,
      sendEmail,
    };

    this.dismiss(item);
  }

  dismiss(item?: News) {
    this._modal.dismiss(item);
  }
}
